import { useRef, useState } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { loadStudents } from '../../../Redux/studentsSlice';
import { loadLessons } from '../../../Redux/lessonsSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import {
  BackupService,
  type CardImportPreview,
  type FullImportPreview
} from '../../../Services/BackupService';
import { localDateString } from '../../../Utils/LessonStatusUtil';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import './Backup.css';

type Mode = 'merge' | 'replace';
type PendingImport =
  | { kind: 'full'; backup: Record<string, unknown>; preview: FullImportPreview }
  | { kind: 'cards'; backup: Record<string, unknown>; preview: CardImportPreview };

/**
 * Taking the data out, and putting it back.
 *
 * Export stays on the free plan: a tutor's own records are theirs, and
 * locking the door out of the product is not what premium is for.
 *
 * Importing is the dangerous direction, so "replace" is never run blind. The
 * server does a dry run first and the confirmation states exactly how many
 * students and how many lessons it is about to remove.
 */
export function Backup() {
  const { tr } = useTranslation();
  const dispatch = useAppDispatch();
  const fileInput = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<Mode>('merge');
  const [pending, setPending] = useState<PendingImport | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function download(name: string, payload: unknown) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${name}-${localDateString()}.json`;
    link.click();
    // Revoking immediately would race the download on some browsers.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  async function exportAll() {
    setError(null);
    try {
      download('magbit-backup', await BackupService.exportAll());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function exportCards() {
    setError(null);
    try {
      download('magbit-students', await BackupService.exportStudentCards());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function pickFile(file: File) {
    setError(null);
    setNotice(null);
    let backup: Record<string, unknown>;
    try {
      backup = JSON.parse(await file.text()) as Record<string, unknown>;
    } catch {
      setError('VALIDATION_FAILED');
      return;
    }
    try {
      if (Array.isArray(backup.payments)) {
        setPending({ kind: 'full', backup, preview: await BackupService.previewImportAll(backup) });
      } else {
        setPending({ kind: 'cards', backup, preview: await BackupService.previewStudentCards(backup, mode) });
      }
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    }
  }

  async function confirmImport() {
    if (!pending) return;
    try {
      if (pending.kind === 'full') {
        await BackupService.importAll(pending.backup);
        setNotice(tr('fullImportDone', pending.preview.incomingStudents, pending.preview.incomingLessons));
      } else {
        const result = await BackupService.importStudentCards(pending.backup, mode);
        setNotice(tr('importDone', result.added, result.replaced, result.removed));
      }
      // The roster and the lessons both moved, so both are reloaded.
      void dispatch(loadStudents());
      void dispatch(loadLessons());
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setPending(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <section className="backup">
      <h1 className="screen-title">{tr('navBackup')}</h1>

      <section className="profile-panel">
        <h2>{tr('exportTitle')}</h2>
        <p className="dashboard-quiet">{tr('exportHint')}</p>
        <div className="backup-actions">
          <button type="button" className="primary-button" onClick={() => void exportAll()}>
            {tr('exportAll')}
          </button>
          <button type="button" className="ghost-button" onClick={() => void exportCards()}>
            {tr('exportCards')}
          </button>
        </div>
      </section>

      <section className="profile-panel">
        <h2>{tr('importTitle')}</h2>
        <p className="dashboard-quiet">{tr('importHint')}</p>

        <fieldset className="backup-modes">
          <legend className="visually-hidden">{tr('importTitle')}</legend>
          <label className="check-label">
            <input type="radio" name="mode" checked={mode === 'merge'} onChange={() => setMode('merge')} />
            <span>{tr('importMerge')}</span>
          </label>
          <label className="check-label">
            <input type="radio" name="mode" checked={mode === 'replace'} onChange={() => setMode('replace')} />
            <span>{tr('importReplace')}</span>
          </label>
        </fieldset>

        <input
          ref={fileInput}
          className="text-input"
          type="file"
          accept=".json,application/json"
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) void pickFile(file);
          }}
        />

        {notice && <p className="profile-saved">{notice}</p>}
      </section>

      {error && <ErrorState code={error} />}

      {pending && (
        <ConfirmDialog
          title={tr('importTitle')}
          // Stated, not estimated: these numbers come from a server dry run.
          body={pending.kind === 'full'
            ? tr(
                'fullImportBody',
                pending.preview.incomingStudents,
                pending.preview.incomingLessons,
                pending.preview.currentStudents,
                pending.preview.currentLessons
              )
            : mode === 'replace'
              ? tr('importReplaceBody', pending.preview.willRemove, pending.preview.willRemoveLessons)
              : tr('importMergeBody', pending.preview.willAdd)}
          confirmLabel={tr('confirm')}
          destructive={pending.kind === 'full' || mode === 'replace'}
          onCancel={() => {
            setPending(null);
            if (fileInput.current) fileInput.current.value = '';
          }}
          onConfirm={() => void confirmImport()}
        />
      )}
    </section>
  );
}
